import type { ListBuilderColumn } from '../../types';
import { MERCHANDISER_TEMPLATE_ACTIVE_VALUE_MAPPING } from './shared';

/** Template name — the row's identity; clicking the row opens the editor. */
export const createTemplateNameColumn = (): ListBuilderColumn => ({
	id: 'merchandiser-template-name',
	headers: [{ id: 'merchandiser-template-name-header', text: 'Name' }],
	fields: [
		{
			id: 'template-name-field',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'name', objectTable: 'merchandiser_template' },
			},
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: true,
	minWidth: 200,
});

/** Free-text note about what the setup is for. */
export const createTemplateDescriptionColumn = (): ListBuilderColumn => ({
	id: 'merchandiser-template-description',
	headers: [
		{ id: 'merchandiser-template-description-header', text: 'Description' },
	],
	fields: [
		{
			id: 'template-description-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'description',
					objectTable: 'merchandiser_template',
				},
				defaultValue: '—',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'muted' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	minWidth: 240,
});

/** How many shelves the template builds. */
export const createTemplateLocationCountColumn = (): ListBuilderColumn => ({
	id: 'merchandiser-template-locations',
	headers: [
		{ id: 'merchandiser-template-locations-header', text: 'Locations' },
	],
	fields: [
		{
			id: 'template-location-count-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'locationCount',
					objectTable: 'merchandiser_template',
				},
				defaultValue: '0',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 110,
});

/** How many of those shelves a sensor module watches. */
export const createTemplateSensorCountColumn = (): ListBuilderColumn => ({
	id: 'merchandiser-template-sensors',
	headers: [{ id: 'merchandiser-template-sensors-header', text: 'Sensors' }],
	fields: [
		{
			id: 'template-sensor-count-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'sensorCount',
					objectTable: 'merchandiser_template',
				},
				defaultValue: '0',
			},
			styleConfig: { size: 'sm', colorRule: { type: 'default' } },
			displayStyle: 'text',
		},
	],
	sortable: false,
	width: 110,
});

/**
 * Active / Retired badge. Rendered from the `activeLabel` string the query
 * service derives, so the badge value mapping stays a plain string match.
 */
export const createTemplateActiveColumn = (): ListBuilderColumn => ({
	id: 'merchandiser-template-active',
	headers: [{ id: 'merchandiser-template-active-header', text: 'Active' }],
	fields: [
		{
			id: 'template-active-field',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'activeLabel',
					objectTable: 'merchandiser_template',
				},
			},
			styleConfig: {
				size: 'sm',
				valueMapping: [...MERCHANDISER_TEMPLATE_ACTIVE_VALUE_MAPPING],
			},
			displayStyle: 'badge',
		},
	],
	sortable: false,
	width: 120,
});

/**
 * Edit / Delete. The template editor is a sidebar rather than a routed detail
 * page, so the actions are callbacks the page supplies (`editTemplate`,
 * `deleteTemplate`) rather than links.
 */
export const createTemplateActionsColumn = (): ListBuilderColumn => ({
	id: 'merchandiser-template-actions',
	headers: [{ id: 'merchandiser-template-actions-header', text: '' }],
	fields: [
		{
			id: 'template-actions-dropdown',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: { size: 'md', colorRule: { type: 'default' } },
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'edit-template-action',
					label: 'Edit',
					iconName: 'edit',
					callbackName: 'editTemplate',
					variant: 'primary',
					visibility: 'visible',
				},
				{
					id: 'delete-template-action',
					label: 'Delete',
					iconName: 'trash',
					callbackName: 'deleteTemplate',
					variant: 'danger',
					visibility: 'visible',
				},
			],
		},
	],
	sortable: false,
	width: 120,
	align: 'center',
});

export const createMerchandiserTemplateColumns =
	(): Array<ListBuilderColumn> => [
		createTemplateNameColumn(),
		createTemplateDescriptionColumn(),
		createTemplateLocationCountColumn(),
		createTemplateSensorCountColumn(),
		createTemplateActiveColumn(),
		createTemplateActionsColumn(),
	];
